import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useController } from "react-hook-form";
import { TextField, Button, Box, Typography } from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const FileInput = React.forwardRef(
  (
    { label, name, control, rules, helperText, onClearInput, setValue },
    ref
  ) => {
    const {
      field: { onChange, value },
      fieldState: { error },
    } = useController({ name, control, rules });

    const allowedFile =
      name === "poster" || name === "author_image"
        ? "image/jpeg, image/png"
        : name === "image"
        ? "image/png"
        : name === "document"
        ? "application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "";

    const [fileName, setFileName] = useState("");
    const [filePreview, setFilePreview] = useState(null);
    const [fileType, setFileType] = useState("");

    const handleFileChange = (event) => {
      const file = event.target.files[0];

      if (file) {
        setFileName(file.name); // Set file name for display
        onChange(Array.from(event.target.files)); // Update react-hook-form state with the FileList

        // Manually update react-hook-form state using setValue
        // setValue(name, event.target.files); // Update the field directly

        // Check the file type and set the preview
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => setFilePreview(reader.result); // Set preview for image files
          reader.readAsDataURL(file);
          setFileType("image");
        } else if (file.type === "application/pdf") {
          setFilePreview(URL.createObjectURL(file)); // Set preview for PDF files
          setFileType("pdf");
        } else {
          setFilePreview(null); // Clear preview for unsupported file types
        }
      }

      // Reset the input field after handling the file to allow re-uploading the same file
      event.target.value = "";
    };

    const fileInputRef = React.useRef(null);

    const handleRemoveFile = () => {
      setFileName(""); // Reset file name
      setFilePreview(null); // Reset file preview
      setFileType(""); // Reset file type
      onChange([]); // Clear react-hook-form field

      // Reset the file input element to allow re-upload of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    useEffect(() => {
      setFileName(""); // Reset file name
      setFilePreview(null); // Reset file preview
      setFileType(""); // Reset file type
    }, [onClearInput]);

    return (
      <Box>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
          sx={{
            backgroundColor: error ? "red" : "#d33333",
            "&:hover": { backgroundColor: error ? "darkred" : "#a62828" },
            textTransform: "none",
            padding: "8px 16px",
            borderRadius: "8px",
            mr: 2,
          }}
        >
          {label}
          <input
            type="file"
            multiple
            accept={allowedFile}
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </Button>

        {fileName && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveFile}
            sx={{
              textTransform: "none",
              padding: "8px 16px",
              borderRadius: "8px",
            }}
          >
            Remove
          </Button>
        )}

        <TextField
          value={fileName}
          InputProps={{ readOnly: true, disableUnderline: true }}
          variant="standard"
          placeholder="No file selected"
          fullWidth
          helperText={error ? error.message : helperText}
          FormHelperTextProps={{
            sx: { color: error ? "red" : "inherit", fontWeight: "bold" },
          }}
        />

        {fileType === "image" && filePreview && (
          <Box
            mt={2}
            sx={{
              position: "relative",
              maxWidth: "auto",
              maxHeight: "auto",
              borderRadius: 1,
            }}
          >
            <Box
              component="img"
              src={filePreview}
              alt="File Preview"
              sx={{ width: "auto", height: "auto", maxHeight: 200, padding: 3 }}
            />
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                p: 1,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
              }}
            >
              Preview:
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

FileInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  helperText: PropTypes.string,
  onClearInput: PropTypes.func,
};

export default FileInput;
